import moment from 'moment-timezone'
import { find } from 'lodash'

// Some devices (Sonos) report a fixed UTC offset such as "+02:00" instead of
// an IANA timezone. moment-timezone knows nothing about those, but Intl does,
// so we keep them as offsets and handle them where moment is involved.
// The hour is not always zero padded and the minutes are sometimes missing
// ("-6:00", "+2"), while Intl only accepts the padded "+HH:MM" form.
const UTC_OFFSET_REGEX = /^([+-])(\d{1,2})(?::(\d{2}))?$/

/**
 * Canonical "+HH:MM" form of a UTC offset, null when it is not one.
 * The range is left to Intl to judge.
 */
export const toCanonicalUTCOffset = (
	timezone?: string | null
): string | null => {
	const parts = timezone?.match(UTC_OFFSET_REGEX)
	if (!parts) {
		return null
	}
	const [, sign, hours, minutes = '00'] = parts
	return `${sign}${hours.padStart(2, '0')}:${minutes}`
}

export const isUTCOffset = (timezone?: string | null): boolean =>
	toCanonicalUTCOffset(timezone) !== null

const formatOffsetMinutes = (offsetMinutes: number) => {
	if (offsetMinutes === 0) {
		return 'UTC'
	}
	const sign = offsetMinutes > 0 ? '+' : '-'
	const hours = Math.floor(Math.abs(offsetMinutes) / 60)
	const minutes = Math.abs(offsetMinutes) % 60
	return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`
}

// Convert a timezone IANA format (e.g. "America/Los_Angeles") or a fixed UTC
// offset (e.g. "+02:00") to UTC+offset format (e.g. "UTC-8", "UTC+5:30")
// Used to display the timezone in the timezone switch button
export const convertTimezoneIANAToUTCFormat = (timezoneName: string) => {
	const offset = toCanonicalUTCOffset(timezoneName)
	if (offset) {
		return formatOffsetMinutes(moment().utcOffset(offset).utcOffset())
	}
	if (moment.tz.zone(timezoneName) != null) {
		return formatOffsetMinutes(moment().tz(timezoneName).utcOffset())
	}
	return 'Unknown'
}

/**
 * Build a moment for an offset-less date string (e.g. "2026-01-01T17:31:00")
 * expressed in the given timezone, which may be an IANA name or a fixed UTC
 * offset. moment.tz silently falls back to UTC for the latter.
 */
export const momentInTimezone = (date: string, timezone: string) => {
	const offset = toCanonicalUTCOffset(timezone)
	return offset ?
			moment.parseZone(`${date}${offset}`)
		:	moment.tz(date, timezone)
}

/**
 * Shift a moment into the given timezone, which may be an IANA name or a
 * fixed UTC offset. The instant is unchanged, only the wall clock it reads on.
 */
export const toTimezone = (date: moment.Moment, timezone: string) => {
	const offset = toCanonicalUTCOffset(timezone)
	return offset ? date.utcOffset(offset) : date.tz(timezone)
}

/**
 * The timezone a device plays its schedule in, as an IANA name or a
 * canonical UTC offset. Null when the device timezone is missing or unknown.
 */
export const resolveDeviceTimezone = (
	timeZone?: string | null
): string | null => {
	const offset = toCanonicalUTCOffset(timeZone)
	if (offset) {
		return offset
	}
	if (!timeZone) {
		return null
	}
	const timezone = String(normalizeTimezoneToIANAFormat(timeZone))
	return moment.tz.zone(timezone) != null ? timezone : null
}

/**
 * The wall clock time of the given timezone, as a browser local Date reading
 * the same hours. Schedules are floating wall clock times displayed as
 * browser local dates, so this is "now" for a player in that timezone.
 */
export const wallClockDateIn = (timezone: string, now: Date = new Date()) =>
	moment(
		toTimezone(moment(now), timezone).format('YYYY-MM-DDTHH:mm:ss.SSS')
	).toDate()

// Resolve the timezones a user can pick from when browsing a device history:
// the (normalized) device timezone and the browser timezone
export const resolveTimezones = (deviceTimeZone?: string | null) => {
	const browserTimezone = moment.tz.guess()
	const deviceTimezone =
		toCanonicalUTCOffset(deviceTimeZone) ||
		(deviceTimeZone && normalizeTimezoneToIANAFormat(deviceTimeZone)) ||
		browserTimezone
	return {
		deviceTimezone,
		browserTimezone,
		isDifferent: deviceTimezone !== browserTimezone
	}
}

// WARNING: it is impossible to reliably guess a IANA TZ name (e.g. "America/Los_Angeles") from the UTC offset (e.g. "-7").
// Multiple TZs can share the same UTC offset at a specific point in time, and diverge later (e.g. because of DST).
// This function is a crutch to work with old Drax players which sometimes report an offset instead of a IANA timezone
const getIANATimezoneFromOffset = (timezoneOffset: number) => {
	const IANATimezone = find(moment.tz.names(), (timezoneName: string) => {
		return timezoneOffset === parseInt(moment.tz(timezoneName).format('Z'))
	})
	return IANATimezone
}

// WARNING: it is impossible to reliably guess a IANA TZ name (e.g. "America/Los_Angeles") from a TZ's abbreviated name (e.g. "PDT")
// Multiple TZs can share the same UTC offset at a specific point in time, and diverge later (e.g. because of DST).
// This function is a crutch to work with old Drax players which sometimes report an abbreviation instead of a IANA timezone
const getIANATimezoneFromAbbreviation = (abbreviation: string) => {
	return find(moment.tz.names(), (timezoneName: string) => {
		const abbrs = moment.tz.zone(timezoneName)?.abbrs
		return abbrs?.includes(abbreviation)
	})
}

// Take any timezone format (abbreviation, UTC, IANA)
// and return the IANA format
// The returned timezone is not reliable and SHOULD NOT be stored
export const normalizeTimezoneToIANAFormat = (timezone: string) => {
	// If the timezone is in the IANA format then return it as it is
	if (moment.tz.zone(timezone) != null) {
		return timezone
	} // If the timezone is in the UTC format then attempt to map
	else if (timezone?.match(/^UTC((\+|-)\d{1,2}(:\d{1,2})?)?$/)) {
		const normalizedTimezone = getIANATimezoneFromOffset(
			parseInt(timezone.replace('UTC', ''))
		)
		if (normalizedTimezone) {
			return normalizedTimezone
		} else {
			return timezone
		}
	}
	// Then if the timezone is in an other format then convert it to IANA
	else {
		// Get the IANA format if it exists
		const normalizedTimezone = getIANATimezoneFromAbbreviation(timezone)
		if (normalizedTimezone) {
			return normalizedTimezone
		} else {
			return timezone
		}
	}
}

export const timeAgo = (input: string | Date) => {
	const date = input instanceof Date ? input : new Date(input)
	const ranges = [
		['years', 3600 * 24 * 365],
		['months', 3600 * 24 * 30],
		['weeks', 3600 * 24 * 7],
		['days', 3600 * 24],
		['hours', 3600],
		['minutes', 60],
		['seconds', 1]
	] as const
	const secondsElapsed = (date.getTime() - Date.now()) / 1000

	for (const [rangeType, rangeVal] of ranges) {
		if (rangeVal < Math.abs(secondsElapsed)) {
			const delta = Math.ceil(secondsElapsed / rangeVal)
			return { rangeType, delta }
		}
	}

	return {}
}

export const getDateFromObjectIdString = (objectId: string) => {
	return new Date(parseInt(objectId.slice(0, 8), 16) * 1000)
}
